import React, { memo, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useErrorHandler } from "react-error-boundary";
import styled from "styled-components";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import { IDisplayAvailablePeriods, IDisplayPeriod } from "../../../types";
import { Loader } from "../../loader/loader";
import { PeriodsView } from "./periods-view";
import { TimeSlotLegend } from "../time-slot-legend/time-slot-legend";
import { GolEventListener } from "../../calendar/calendar";
import { InputWithIcon } from "../../input-with-icon/input-with-icon";
import { selectionStore } from "../../../stores/selection.store";
import { getTagManagerService } from "../../../services/tag-manager.service";
import { selectionStoreChangesService } from "../../../services/selection-store-changes.service";
import iDisplayPeriodsService from "../../../../src/services/i-display-periods-builder.service";
import ReactPixel from "react-facebook-pixel";
import { moduleSettings } from "../../../stores/module-settings.store";

const SelectionTimeWrapper = styled.div`
  position: relative;
  margin: 0 6px;
  display: flex;
  width: 300px;
  flex-direction: column;
  align-items: center;
`;

const MessageInfo = styled.div`
  font-size: 14px;
  background-color: ${(props) => props.theme.backgroundColor};
  border-radius: 4px;
  padding: 12px;
`;

const Anchor = styled.div`
  width: 1px;
  height: 1px;
`;

const Time = () => {
  const { t, i18n } = useTranslation();
  const displayPeriods = useRef<Array<IDisplayPeriod>>([]);
  const filters = useRef<any>({});
  const hasPeriods = useRef(false);
  const engineMode = useRef(useLocation().state as { [key: string]: string });
  const [message, setMessage] = useState("");
  const [requestingData, setRequestingData] = useState(false);
  const [selectedDate, setSelectedDate] = useState(selectionStore.date);
  const [selectedTime, setSelectedTime] = useState(selectionStore.time);
  const [initialDate, setInitialDate] = useState(selectedDate);
  const shouldHideLegend = moduleSettings.shouldHideLegend;

  const throwError = useErrorHandler();

  const checkPeriodAndTime = (periods: IDisplayPeriod[]): void => {
    if (!selectedTime) return;
    let hours: any = [];
    periods.forEach((period) => {
      if (period.hours) {
        hours.push(period.hours.map((hour) => hour.value));
      }
    });
    if (!hours.flat().includes(selectedTime)) {
      setSelectedTime("");
      selectionStore.time = "";
      selectionStoreChangesService.dispatch(GolEventListener.Time);
    }
  };

  const isInViewport = (element) => {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  const scrollToAnchor = () => {
    const anchor = document.querySelector("#anchor-selection-time");
    if (!isInViewport(anchor)) {
      anchor?.scrollIntoView({ block: "end", inline: "nearest" });
    }
  };

  useEffect(() => {
    selectionStoreChangesService.registerListener(
      "selection-on-complete",
      [
        GolEventListener.CalendarDate,
        GolEventListener.OnComplete,
        GolEventListener.Area,
      ],
      () => {
        setSelectedDate(selectionStore.date);
        if (selectionStore.date && selectionStore.date.isValid()) {
          // Check if area selection is required but no area is selected
          const shouldSkipRequest =
            moduleSettings.forceAreaSelection &&
            (selectionStore.area === null ||
              selectionStore.area === "placeholder");

          if (!shouldSkipRequest) {
            setRequestingData(true);
          }
        }
      }
    );

    return () => {
      selectionStoreChangesService.unregisterListener("selection-on-complete");
    };
  }, []);

  useEffect(() => {
    selectionStoreChangesService.registerListener(
      "selection-time",
      [GolEventListener.Time, GolEventListener.OnComplete],
      () => {
        setSelectedTime(selectionStore.time);
      }
    );

    return () => {
      selectionStoreChangesService.unregisterListener("selection-time");
    };
  }, [selectedDate]);

  useEffect(() => {
    if (!selectedDate) return;
    if (!requestingData) return;
    displayPeriods.current = [];
    hasPeriods.current = false;
    const subscription = iDisplayPeriodsService
      .getAvailableIDisplayPeriods(engineMode.current)
      .subscribe({
        next: (resp: IDisplayAvailablePeriods) => {
          const periods = resp.displayPeriods;
          displayPeriods.current = resp.displayPeriods;
          filters.current = resp.filters;
          hasPeriods.current = periods.length !== 0;
          setMessage(resp.message);
          checkPeriodAndTime(periods);
          getTagManagerService().pushTags("hours");
          ReactPixel.pageView();
          setRequestingData(false);
          if (initialDate !== selectedDate) {
            scrollToAnchor();
            setInitialDate(selectedDate);
          }
        },
        error: (error) => {
          setMessage(error.data.message);
          setRequestingData(false);
        },
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [requestingData, i18n.language, throwError]);

  const messageInfo = message
    ? message
    : t("instabook.messages.booking_not_available_for_people_table_engine");

  // Check if area selection is required but no area is selected
  const shouldShowAreaMessage =
    moduleSettings.forceAreaSelection &&
    (selectionStore.area === null || selectionStore.area === "placeholder");

  const content = selectedDate && selectedDate.isValid() && (
    <div>
      {shouldShowAreaMessage && (
        <MessageInfo>
          {t("instabook.errors.area_selection_required")}
        </MessageInfo>
      )}
      {!shouldShowAreaMessage && !hasPeriods.current && (
        <MessageInfo>{messageInfo}</MessageInfo>
      )}
      {!shouldShowAreaMessage && hasPeriods.current && (
        <PeriodsView displayPeriods={displayPeriods.current} />
      )}
      {!shouldShowAreaMessage && hasPeriods.current && !shouldHideLegend && (
        <TimeSlotLegend />
      )}
    </div>
  );

  return (
    <SelectionTimeWrapper data-test="selection-time" id="selection-time">
      <InputWithIcon
        error={false}
        value={selectedTime}
        placeholderKey={"instabook.bookings.hour"}
        helperText={""}
        variant={"outlined"}
        icon={<AccessTimeIcon />}
        isSummary={true}
      />
      {requestingData ? <Loader /> : content}
      <Anchor id="anchor-selection-time" />
    </SelectionTimeWrapper>
  );
};
export const SelectionTime = memo(Time);
